/**
 * Reader text size — Next Steps only.
 *
 * Deliberately NOT an app-wide setting, and deliberately NOT driven by the OS.
 *
 * The app's layouts are built from fixed-px boxes, so scaling every screen means
 * auditing every one of them; and following iOS's Dynamic Type means the app
 * changes size without anyone here choosing it, at sizes that reach 3.12x. Both
 * were tried and both broke real screens. Scoping the scale to the Next Steps
 * routes keeps it where it earns its keep — long-form lesson reading — and keeps
 * the rest of the app at exactly the size it was designed at.
 *
 * `--font-scale` is written onto <html> (see src/theme/font-scale.css) while a
 * Next Steps route is active, and reset to 1 on the way out. It is on the root
 * rather than a container because the type is sized in rem, which resolves
 * against the root — and because sheets and modals teleport out of the page
 * subtree and should scale with the reader they were opened from.
 */

export const STORAGE_KEY = 'hogc:reader-font-scale';

/**
 * Six sizes, and the three constants that produce them:
 *
 *   0.875   1     1.125   1.25   1.375   1.5
 *             ^ DEFAULT_SCALE
 *
 * Six rather than the fourteen a 0.05 step gave. Fourteen stops is a continuous
 * control in all but name, and this one is not continuous: every stop costs a
 * full-document reflow, so half of them were travel a member paid for and could
 * not really see. Six are far enough apart that each one is a visibly different
 * size — a size you pick, not a value you tune.
 *
 * MIN is where tap targets and hairlines start to break down; MAX is where the
 * lesson card and the reader chrome stop holding together. The step is what
 * divides the distance between them into five, and it has to land exactly on
 * DEFAULT_SCALE so the design size stays reachable by dragging — 0.125 is the
 * only spacing that does that and reaches both ends, which is why MIN is 0.875
 * and not the 0.85 the floor was first set at.
 */
export const MIN_SCALE = 0.875;
export const MAX_SCALE = 1.5;
export const STEP = 0.125;

/** The size to return to. Also the size every screen outside Next Steps uses. */
export const DEFAULT_SCALE = 1;

/** How many sizes the slider offers. Derived, so it cannot drift from the band. */
export const STEP_COUNT = Math.round((MAX_SCALE - MIN_SCALE) / STEP) + 1;

/**
 * Which routes the scale applies to. Everything under /courses — the part
 * chooser, the lesson list, the reader, the notes hub and the leader views —
 * so the size does not jump as a member moves between them.
 * @param {string} path
 * @returns {boolean}
 */
export function isScaledRoute(path) {
  return typeof path === 'string' && path.startsWith('/courses');
}

/**
 * @param {number} value
 * @param {number} [max] - raised only by the dev harness, which exists to find
 *   where the layout gives out
 * @returns {number}
 */
export function clampScale(value, max = MAX_SCALE) {
  // null and '' both coerce to 0, which is finite — so a Number()-only guard
  // would floor them to MIN_SCALE and quietly shrink the reader instead of
  // falling back to the design size.
  if (value === null || value === undefined || value === '') return 1;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.min(Math.max(n, MIN_SCALE), max);
}

/**
 * Round to one of the six sizes on the track.
 *
 * A member's stored size is always one of them, so the thumb and the reader can
 * never disagree — and a size stored by an older build, off the current track
 * (the step used to be 0.05), lands on the nearest one it offers rather than
 * leaving the slider pointing at a size the reader is not actually using.
 *
 * Deliberately NOT part of clampScale: the dev harness previews arbitrary sizes
 * to find where layouts give out, and snapping those would defeat the point.
 * @param {number} value
 * @returns {number}
 */
export function snapScale(value) {
  const n = clampScale(value);
  const steps = Math.round((n - MIN_SCALE) / STEP);
  // 0.875 and 0.125 are both exact in binary, so every stop comes out clean —
  // no 1.1250000000000002 to round away.
  return Math.min(Math.max(MIN_SCALE + steps * STEP, MIN_SCALE), MAX_SCALE);
}

/**
 * The only function that touches the DOM.
 *
 * Writing the same size twice is not free: setProperty dirties the element's
 * style whether or not the value changed, and this particular property is
 * `html`'s font-size, so the browser then relayouts the entire document for a
 * size it was already at. A drag arrives as a stream of `input` events, plenty
 * of which resolve to the size already applied — the guard makes those cost
 * nothing.
 * @param {number} value
 * @returns {number} the value applied
 */
export function applyScale(value) {
  const scale = clampScale(value, Infinity); // callers clamp for themselves
  const style = document.documentElement.style;
  const next = String(scale);
  if (style.getPropertyValue('--font-scale') !== next) {
    style.setProperty('--font-scale', next);
  }
  return scale;
}

/**
 * The member's stored choice. Defaults to 1 — no size changes until someone
 * asks for one.
 * @returns {number}
 */
export function readPreference() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === null ? 1 : snapScale(Number(raw));
  } catch {
    return 1;
  }
}

/**
 * @param {number} value
 */
export function writePreference(value) {
  try {
    localStorage.setItem(STORAGE_KEY, String(snapScale(value)));
  } catch {
    // Private mode or storage full — the scale still applies for this session.
  }
}
